import { useSite } from "../lib/site";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { FieldLabel, Sel } from "./Fields";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { getAttribution } from "../lib/store";
import { formatSaPhone, isValidSaPhone } from "../lib/format";

export function useContactFields() {
  const [v, setV] = useState({ name: "", phone: "", email: "", consent: false, website: "" });
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: k === "phone" ? formatSaPhone(e.target.value) : e.target.value }));
  return [v, setV, set];
}

export function ContactFields({ v, set, setV, prefix }) {
  const { t } = useI18n();
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor={`${prefix}-name`}>{t("form.name")} *</FieldLabel>
          <input id={`${prefix}-name`} data-testid={`${prefix}-name-input`} className="field" required minLength={2} maxLength={100} value={v.name} onChange={set("name")} autoComplete="name" />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-phone`}>{t("form.phone")} *</FieldLabel>
          <input id={`${prefix}-phone`} data-testid={`${prefix}-phone-input`} className="field" dir="ltr" type="tel" inputMode="tel" required placeholder={t("form.phoneHint")} value={v.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
      </div>
      <div>
        <FieldLabel htmlFor={`${prefix}-email`} optional>{t("form.email")}</FieldLabel>
        <input id={`${prefix}-email`} data-testid={`${prefix}-email-input`} className="field" dir="ltr" type="email" maxLength={200} value={v.email} onChange={set("email")} autoComplete="email" />
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" value={v.website} onChange={set("website")} data-testid={`${prefix}-honeypot`} />
    </>
  );
}

export function ConsentBox({ v, setV, prefix }) {
  const { t } = useI18n();
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={`${prefix}-consent`} data-testid={`${prefix}-consent-checkbox`} checked={v.consent} onCheckedChange={(c) => setV((s) => ({ ...s, consent: !!c }))} className="mt-0.5 border-cta/60 data-[state=checked]:bg-cta data-[state=checked]:text-canvas" />
      <label htmlFor={`${prefix}-consent`} className="text-xs leading-relaxed text-fg/60">
        {t("form.consentA")} <Link to="/privacy" className="text-cta hover:underline">{t("form.privacy")}</Link> {t("form.consentB")}
      </label>
    </div>
  );
}

export function validateContact(v, t) {
  if (v.name.trim().length < 2 || !v.phone) return t("form.required");
  if (!isValidSaPhone(v.phone)) return t("form.invalidPhone");
  if (!v.consent) return t("form.consentReq");
  return null;
}

export const trackingPayload = (lang) => ({ ...getAttribution(), lang });

export default function LeadForm({ type = "inquiry", propertyId, projectId, defaultMessage = "", prefix = "lead", investor = false, submitLabel }) {
  const { t, lang, tr } = useI18n();
  const [v, setV, set] = useContactFields();
  const [message, setMessage] = useState(defaultMessage);
  const [extra, setExtra] = useState({ budget: "", interest: "", department: "" });
  const { settings } = useSite();
  const depts = (settings?.department_emails || []).filter((d) => d.in_form);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateContact(v, t);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const source = type === "inquiry" && propertyId ? "property_inquiry" : `${type}_form`;
      await api.post("/leads", { ...trackingPayload(lang), source, ...v, email: v.email || null, message: message || null, type,
        property_id: propertyId || null, project_id: projectId || null, budget: extra.budget || null, interest: extra.interest || null, department: extra.department || null });
      toast.success(t("form.success"));
      setDone(true);
      setV({ name: "", phone: "", email: "", consent: false, website: "" });
      setMessage(defaultMessage);
    } catch (ex) {
      toast.error(`${t("form.error")}: ${errMsg(ex)}`);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} noValidate data-testid={`${prefix}-form`} className="relative space-y-4">
      <ContactFields v={v} set={set} setV={setV} prefix={prefix} />
      {type === "contact" && depts.length > 0 && (
        <div>
          <FieldLabel htmlFor={`${prefix}-department`} optional>{lang === "ar" ? "القسم" : "Department"}</FieldLabel>
          <Sel testid={`${prefix}-department-select`} value={extra.department} onChange={(d) => setExtra((s) => ({ ...s, department: d }))} placeholder={lang === "ar" ? "اختر القسم" : "Select department"}
            options={depts.map((d) => [d.key, tr(d, "label")])} />
        </div>
      )}
      {investor && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor={`${prefix}-budget`} optional>{t("form.budget")}</FieldLabel>
            <Sel testid={`${prefix}-budget-select`} value={extra.budget} onChange={(b) => setExtra((s) => ({ ...s, budget: b }))} placeholder={t("search.any")}
              options={[["<5M", "< SAR 5M"], ["5-20M", "SAR 5M – 20M"], ["20-50M", "SAR 20M – 50M"], [">50M", "> SAR 50M"]]} />
          </div>
          <div>
            <FieldLabel htmlFor={`${prefix}-interest`} optional>{t("form.interest")}</FieldLabel>
            <Sel testid={`${prefix}-interest-select`} value={extra.interest} onChange={(b) => setExtra((s) => ({ ...s, interest: b }))} placeholder={t("search.any")}
              options={["land", "income", "offplan", "jv"].map((k) => [k, t(`form.interests.${k}`)])} />
          </div>
        </div>
      )}
      <div>
        <FieldLabel htmlFor={`${prefix}-message`} optional>{t("form.message")}</FieldLabel>
        <textarea id={`${prefix}-message`} data-testid={`${prefix}-message-input`} className="field min-h-[110px] resize-y" maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <ConsentBox v={v} setV={setV} prefix={prefix} />
      <button type="submit" disabled={busy} data-testid={`${prefix}-submit-button`} className="btn-cta w-full">{busy ? t("form.sending") : submitLabel || t("form.submit")}</button>
      {done && <p role="status" data-testid={`${prefix}-success-message`} className="text-center text-sm text-cta">{t("form.success")}</p>}
    </form>
  );
}
